import type { PostHog } from 'posthog-js/dist/module.slim';
import type * as Client from './posthog-client';

// PostHog product analytics: slim build (we only send our own events),
// cookieless (no cookies or storage, so no consent banner), production builds
// only, and loaded after the game so it never slows the first paint.
// Project "Politikdle.sk" on PostHog EU; its project key is public by design.
// Events go through PostHog's managed proxy on k.politikdle.sk (a CNAME in the
// Cloudflare zone) so ad blockers don't drop them.
// VITE_POSTHOG_KEY / VITE_POSTHOG_HOST override them, an empty key turns it off.
const KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ?? 'phc_iTdDtRhLo67QDrkn4gpndrKJCG8ZTYTDv8D7tvCUGqu';
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://k.politikdle.sk';

type Props = Parameters<PostHog['capture']>[1];

const ENABLED = Boolean(KEY) && import.meta.env.PROD;

let client: Promise<{ posthog: PostHog; lib: typeof Client } | null> | null = null;

function loadClient() {
  client ??= ENABLED
    ? import('./posthog-client').then((lib) => ({ posthog: lib.createClient(KEY, HOST), lib })).catch(() => null)
    : Promise.resolve(null);
  return client;
}

function load(): Promise<PostHog | null> {
  return loadClient().then((c) => c?.posthog ?? null);
}

export function initAnalytics(): void {
  void load();
}

// Error tracking. Listen from the first moment, before PostHog has loaded, and
// forward once it has. Capped so a broken timer can't flood the project.
const MAX_ERRORS_PER_PAGE = 5;
let errorsSent = 0;

function reportError(error: unknown): void {
  if (errorsSent >= MAX_ERRORS_PER_PAGE) return;
  errorsSent++;
  void loadClient().then((c) => c?.posthog.capture('$exception', c.lib.exceptionProperties(error)));
}

if (ENABLED) {
  // Without an error object it's a cross-origin script (an extension, say): not ours.
  window.addEventListener('error', (e) => e.error && reportError(e.error));
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason));
}

export function track(event: string, props?: Props): void {
  void load().then((posthog) => posthog?.capture(event, props));
}

/**
 * Sends an event right away and reports whether it could have reached PostHog.
 * capture() can't tell us, so first probe the host: ad blockers block it
 * outright, which makes the no-cors fetch reject.
 */
export async function sendNow(event: string, props: Props): Promise<boolean> {
  const posthog = await load();
  if (!posthog) return false;
  try {
    await fetch(HOST, { mode: 'no-cors', cache: 'no-store' });
  } catch {
    return false;
  }
  posthog.capture(event, props, { send_instantly: true });
  return true;
}
